import { Injectable, Inject } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "./api.service";

import { environment } from "@env";
import { IContextCompany, IContextProperty, IContextCompanyJson, IContextSubscriptionJson } from "@schema/context";
import { IHttpResponse, IHttpRequestOptions } from "@schema/http";

import { SESSION_STORAGE, StorageService, StorageTranscoders } from "ngx-webstorage-service";
import { sortEntityByName } from "@shared/utility/sort-entity-by-name";


@Injectable({
  providedIn: "root"
})
export class ContextService {

  /**
   * The key for storing in local/session storage
   */
  readonly STORAGE_KEY = "appContext";

  /**
   * The current company, if any
   */
  public company$: BehaviorSubject<IContextCompany> = new BehaviorSubject(null);

  /**
   * The current property, if any
   */
  public property$: BehaviorSubject<IContextProperty> = new BehaviorSubject(null);

  /**
   * If there is currently context or not
   */
  public hasContext$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    protected _apiService: ApiService,
    @Inject(SESSION_STORAGE) protected _storage: StorageService
  ) { }

  public loadContexts(options?: IHttpRequestOptions): Observable<IContextCompany[]> {
    return this._apiService.get<IContextCompanyJson[]>(
      `${environment.api.globalHost}/2019-03-01/me/assignments`, options
    ).pipe(
      // TODO catchError( )
      map((response: IHttpResponse<IContextCompanyJson[]>): IContextCompany[] => {
        return response.data.map((company: IContextCompanyJson): IContextCompany => {
          const id = company.id;
          const name = company.name;

          // Grab just the properties, we don't need subscription info (currently)
          let properties = [];
          company.subscriptions.forEach((subscription: IContextSubscriptionJson) => {
            properties = properties.concat(subscription.properties);
          });
          properties.sort(sortEntityByName);

          return { id, name, properties } as IContextCompany;
        });
      }),
    );
  }

  public setContext(company: IContextCompany, property: IContextProperty): void {
    this.company$.next(company);
    this.property$.next(property);
    this.hasContext$.next(true);

    // Store the selected company / property into local/session storage for ease of use
    // TODO: move this server-side
    this._storage.set(this.STORAGE_KEY, {
      company: company.id,
      property: property.id
    }, StorageTranscoders.JSON);
  }

  public clearContext(): void {
    this.company$.next(null);
    this.property$.next(null);
    this.hasContext$.next(false);

    this._storage.remove(this.STORAGE_KEY);
  }
}
